import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  LinearProgress,
  Stack,
  Paper,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { Document, DocumentType, User } from '../../types';
import { DocumentService } from '../../services/documentService';

interface DocumentUploadProps {
  personId: string;
  personName: string;
  currentUser: User;
  onUploadComplete: (document: Document) => void;
  onCancel?: () => void;
  maxFileSize?: number; // in MB, default 10MB
  acceptedFileTypes?: string[];
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  personId,
  personName,
  currentUser,
  onUploadComplete,
  onCancel,
  maxFileSize = 10,
  acceptedFileTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/tiff',
    'image/tif',
    'application/pdf',
  ],
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<DocumentType>(DocumentType.OTHER);
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate file
  const validateFile = (file: File): string | null => {
    if (!acceptedFileTypes.includes(file.type)) {
      return `File type ${file.type} is not supported. Please use: PDF, JPEG, PNG, or TIFF`;
    }

    if (file.size > maxFileSize * 1024 * 1024) {
      return `File size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds maximum of ${maxFileSize}MB`;
    }

    return null;
  };

  // Handle file selection
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);

      // Auto-populate title if empty
      if (!title) {
        const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
        setTitle(fileName);
      }
    },
    [title, maxFileSize, acceptedFileTypes]
  );

  // Handle file drop
  const handleFileDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const file = event.dataTransfer.files?.[0];
      if (!file) return;

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);

      // Auto-populate title if empty
      if (!title) {
        const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
        setTitle(fileName);
      }
    },
    [title, maxFileSize, acceptedFileTypes]
  );

  // Prevent default drag behavior
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  // Remove selected file
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Handle upload
  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    if (!title.trim()) {
      setError('Please enter a title for the document');
      return;
    }

    if (!type) {
      setError('Please select a document type');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const metadata = {
        title: title.trim(),
        type,
        description: description.trim(),
        date: date || undefined,
        location: location.trim() || undefined,
      };

      const uploadedDocument = await DocumentService.uploadDocument(
        selectedFile,
        personId,
        personName,
        metadata,
        currentUser.uid,
        currentUser.displayName,
        currentUser.role
      );

      // Simulate progress for better UX (Firebase doesn't provide upload progress for this operation)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      // Complete progress
      setUploadProgress(100);
      clearInterval(progressInterval);

      // Call completion handler
      onUploadComplete(uploadedDocument);

      // Reset form
      setSelectedFile(null);
      setTitle('');
      setType(DocumentType.OTHER);
      setDescription('');
      setDate('');
      setLocation('');
      setUploadProgress(0);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, title, type, description, date, location, personId, personName, currentUser, onUploadComplete]);

  return (
    <Box>
      <DialogTitle>
        Upload Document
        {onCancel && (
          <IconButton
            aria-label="close"
            onClick={onCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Error message */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* File upload area */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Document File *
            </Typography>
            {!selectedFile ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: 'background.default',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                onDrop={handleFileDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  Drag and drop a document here, or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: PDF, JPEG, PNG, TIFF (max {maxFileSize}MB)
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept={acceptedFileTypes.join(',')}
                  onChange={handleFileSelect}
                />
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <FileIcon color="primary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                <IconButton size="small" onClick={handleRemoveFile} disabled={isUploading}>
                  <CloseIcon />
                </IconButton>
              </Paper>
            )}
          </Box>

          {/* Upload progress */}
          {isUploading && (
            <Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}

          {/* Metadata fields */}
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            disabled={isUploading}
            helperText="Brief descriptive title for this document"
          />

          <FormControl fullWidth required disabled={isUploading}>
            <InputLabel>Document Type</InputLabel>
            <Select value={type} label="Document Type" onChange={(e) => setType(e.target.value as DocumentType)}>
              <MenuItem value="birth-certificate">Birth Certificate</MenuItem>
              <MenuItem value="death-certificate">Death Certificate</MenuItem>
              <MenuItem value="marriage-certificate">Marriage Certificate</MenuItem>
              <MenuItem value="divorce-decree">Divorce Decree</MenuItem>
              <MenuItem value="census-record">Census Record</MenuItem>
              <MenuItem value="military-record">Military Record</MenuItem>
              <MenuItem value="will">Will</MenuItem>
              <MenuItem value="deed">Deed</MenuItem>
              <MenuItem value="letter">Letter</MenuItem>
              <MenuItem value="diary">Diary</MenuItem>
              <MenuItem value="obituary">Obituary</MenuItem>
              <MenuItem value="newspaper-clipping">Newspaper Clipping</MenuItem>
              <MenuItem value="immigration-record">Immigration Record</MenuItem>
              <MenuItem value="naturalization-papers">Naturalization Papers</MenuItem>
              <MenuItem value="school-record">School Record</MenuItem>
              <MenuItem value="employment-record">Employment Record</MenuItem>
              <MenuItem value="medical-record">Medical Record</MenuItem>
              <MenuItem value="tax-record">Tax Record</MenuItem>
              <MenuItem value="bible-record">Bible Record</MenuItem>
              <MenuItem value="church-record">Church Record</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={isUploading}
            helperText="Additional details about this document"
          />

          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            disabled={isUploading}
            InputLabelProps={{ shrink: true }}
            helperText="Date mentioned in or on the document (if applicable)"
          />

          <TextField
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            fullWidth
            disabled={isUploading}
            helperText="Location mentioned in the document (if applicable)"
          />

          <Typography variant="caption" color="text.secondary">
            * Required fields
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={isUploading}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || !title.trim() || isUploading}
          startIcon={<UploadIcon />}
        >
          {isUploading ? 'Uploading...' : 'Upload Document'}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default DocumentUpload;
